/**
 * v135 — 1200×630 OG-card SVG composer.
 *
 * Renders a Sparrow-branded share card matching the v126 OG visual language
 * (BC-hybrid CFD + isobars + ember-on-near-black). Three discriminated
 * variants:
 *
 *   variant === 'build'   → arrow silhouette + 5 marquee stats + sight-tape mini
 *   variant === 'session' → target overlay with shot dots + aggregate stats
 *   variant === 'compare' → multi-color target overlay + per-source legend
 *
 * All three carry:
 *   - "SE-***-LIVE" classification banner (v110 visual language)
 *   - Sparrow Concierge emblem bottom-right (free brand impression)
 *   - "powered by The Forge · sparrowexpeditions.com" pin
 *   - Optional custom label (top-right, only when privacy allows)
 *
 * Why pure SVG (no foreignObject, no HTML): foreignObject doesn't rasterize
 * reliably on iOS Safari, and the share card MUST work on iPhone because
 * that's where messaging traffic actually happens.
 *
 * Fonts: ui-monospace / ui-sans-serif stack — system fonts, no external load.
 * No webfont = no FOUT in the rasterized output.
 */

import { useRef } from 'react';
import type { BuildState } from '@/types';
import type { ShareSnapshot, ShareCompareSource } from './types';

const W = 1200;
const H = 630;

/** Sparrow color tokens — match Tailwind theme used elsewhere. */
const COLORS = {
  bg: '#0a0a0a',
  bgGrid: '#1a1a1a',
  bgAccent: '#13110a',
  fg: '#fafafa',
  fgMuted: '#a1a1aa',
  fgDim: '#71717a',
  ember: '#f59e0b',
  emberDim: '#a16207',
  cyan: '#22d3ee',
  cyanDim: '#0e7490',
  green: '#10b981',
  rose: '#f43f5e',
  panelStroke: '#27272a',
};

interface ShareCardSVGProps {
  snapshot: ShareSnapshot;
  /** When true, the optional custom label is rendered. */
  showLabel?: boolean;
  /** When true (session variant), photo background is rendered. */
  showPhoto?: boolean;
  /** Imperative-handle ref for the underlying SVG element (rasterizer reads it). */
  svgRef?: React.Ref<SVGSVGElement>;
}

/**
 * Top-level share card. Routes to per-variant body renderer.
 */
export function ShareCardSVG({ snapshot, showLabel, showPhoto, svgRef }: ShareCardSVGProps) {
  const innerRef = useRef<SVGSVGElement | null>(null);
  return (
    <svg
      ref={(node) => {
        innerRef.current = node;
        if (typeof svgRef === 'function') svgRef(node);
        else if (svgRef && 'current' in svgRef) {
          (svgRef as React.MutableRefObject<SVGSVGElement | null>).current = node;
        }
      }}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', width: '100%', height: 'auto' }}
    >
      <Defs />
      <Background />
      <ClassificationBanner snapshot={snapshot} />
      {snapshot.variant === 'build' && (
        <BuildBody snapshot={snapshot} showLabel={showLabel ?? false} />
      )}
      {snapshot.variant === 'session' && (
        <SessionBody
          snapshot={snapshot}
          showLabel={showLabel ?? false}
          showPhoto={showPhoto ?? false}
        />
      )}
      {snapshot.variant === 'compare' && (
        <CompareBody snapshot={snapshot} showLabel={showLabel ?? false} />
      )}
      <SparrowEmblem />
      <FooterPin />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared chrome
// ─────────────────────────────────────────────────────────────────────────────

function Defs() {
  return (
    <defs>
      <linearGradient id="spx-bg-grad" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={COLORS.bg} />
        <stop offset="100%" stopColor={COLORS.bgAccent} />
      </linearGradient>
      <linearGradient id="spx-ember-grad" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={COLORS.ember} stopOpacity="0.9" />
        <stop offset="100%" stopColor={COLORS.emberDim} stopOpacity="0.4" />
      </linearGradient>
      <radialGradient id="spx-vignette" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="#000" stopOpacity="0" />
        <stop offset="100%" stopColor="#000" stopOpacity="0.45" />
      </radialGradient>
      {/* Subtle dot grid */}
      <pattern id="spx-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1" fill={COLORS.bgGrid} />
      </pattern>
    </defs>
  );
}

function Background() {
  return (
    <>
      <rect width={W} height={H} fill="url(#spx-bg-grad)" />
      <rect width={W} height={H} fill="url(#spx-grid)" opacity="0.5" />
      <rect width={W} height={H} fill="url(#spx-vignette)" />
    </>
  );
}

/**
 * Top banner — classification tag in the v110 mil-spec style:
 *   "SE-BLD-LIVE · setup snapshot"
 *   "SE-RNG-LIVE · session"
 *   "SE-CMP-LIVE · comparison"
 */
function ClassificationBanner({ snapshot }: { snapshot: ShareSnapshot }) {
  const text =
    snapshot.variant === 'build'
      ? 'SE-BLD-LIVE · setup snapshot'
      : snapshot.variant === 'session'
        ? 'SE-RNG-LIVE · range session'
        : 'SE-CMP-LIVE · setup comparison';
  return (
    <g>
      <rect x={40} y={32} width={420} height={36} rx={4} fill="#000" opacity="0.6" />
      <rect x={40} y={32} width={6} height={36} fill={COLORS.ember} />
      <text
        x={60}
        y={56}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="14"
        letterSpacing="3"
        fill={COLORS.ember}
      >
        {text.toUpperCase()}
      </text>
    </g>
  );
}

/**
 * Bottom-right emblem — Sparrow Concierge mark (v127 visual). Mono-color
 * silhouette so the rasterizer never has to fetch an external image.
 */
function SparrowEmblem() {
  // Compact lockup: emblem + wordmark
  const cx = W - 130;
  const cy = H - 80;
  return (
    <g transform={`translate(${cx}, ${cy})`}>
      {/* Stylized arrow + sparrow wing — abstract mark */}
      <g fill="none" stroke={COLORS.ember} strokeWidth="2">
        <path d="M 0 0 L 24 -10 L 48 0 L 24 10 Z" fill={COLORS.ember} />
        <path d="M 24 -10 L 70 -28" />
        <path d="M 24 10 L 70 28" />
      </g>
      <text
        x={90}
        y={5}
        fontFamily="ui-sans-serif, -apple-system, BlinkMacSystemFont, system-ui, sans-serif"
        fontSize="20"
        fontWeight="600"
        fill={COLORS.fg}
        letterSpacing="2"
      >
        SPARROW
      </text>
      <text
        x={90}
        y={28}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="11"
        fill={COLORS.fgMuted}
        letterSpacing="4"
      >
        EXPEDITIONS
      </text>
    </g>
  );
}

/** "powered by The Forge · sparrowexpeditions.com" pin (bottom-left). */
function FooterPin() {
  return (
    <g>
      <text
        x={40}
        y={H - 30}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="13"
        fill={COLORS.fgDim}
        letterSpacing="2"
      >
        POWERED BY <tspan fill={COLORS.ember}>THE FORGE</tspan>
      </text>
      <text
        x={40}
        y={H - 12}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="11"
        fill={COLORS.fgMuted}
        letterSpacing="1.5"
      >
        sparrowexpeditions.com
      </text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Build variant
// ─────────────────────────────────────────────────────────────────────────────

function BuildBody({
  snapshot,
  showLabel,
}: {
  snapshot: Extract<ShareSnapshot, { variant: 'build' }>;
  showLabel: boolean;
}) {
  const d = snapshot.derived;
  const stats = [
    { label: 'FPS', value: format(d.estimatedFps, 0) },
    { label: 'Total gr', value: format(d.weight, 0) },
    { label: 'FOC %', value: format(d.foc, 1) },
    { label: 'KE @ 0', value: format(d.ke, 1) },
    { label: 'Mtm', value: format(d.momentum, 2) },
  ];
  return (
    <>
      {showLabel && snapshot.customLabel && (
        <CustomLabel text={snapshot.customLabel} />
      )}
      {/* Arrow silhouette */}
      <ArrowSilhouette y={155} state={snapshot.state} />
      {/* Stats row */}
      <g transform={`translate(80, 380)`}>
        {stats.map((s, i) => (
          <g key={s.label} transform={`translate(${i * 215}, 0)`}>
            <text
              fontFamily="ui-monospace, Menlo, Consolas, monospace"
              fontSize="13"
              letterSpacing="4"
              fill={COLORS.fgDim}
            >
              {s.label.toUpperCase()}
            </text>
            <text
              y={48}
              fontFamily="ui-sans-serif, system-ui, sans-serif"
              fontSize="44"
              fontWeight="700"
              fill={COLORS.fg}
            >
              {s.value}
            </text>
          </g>
        ))}
      </g>
      {/* Tagline */}
      {snapshot.tagline && (
        <text
          x={80}
          y={490}
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontSize="20"
          fill={COLORS.fgMuted}
          fontStyle="italic"
        >
          {truncate(snapshot.tagline, 70)}
        </text>
      )}
    </>
  );
}

/** Simplified arrow silhouette — shaft + vanes + insert + point/broadhead. */
function ArrowSilhouette({ y, state }: { y: number; state: BuildState }) {
  // Coordinate frame: arrow centerline at y, drawn 1040 px wide.
  const x0 = 80;
  const x1 = W - 80;
  const len = x1 - x0;
  const shaftH = 12;
  // Section ranges (% of length, nock → point)
  const nockEnd = 0.04;
  const wrapEnd = 0.10;
  const vaneEnd = 0.22;
  const insertStart = 0.93;
  const pointStart = 0.97;

  return (
    <g>
      {/* Shaft */}
      <rect
        x={x0 + len * nockEnd}
        y={y - shaftH / 2}
        width={len * (insertStart - nockEnd)}
        height={shaftH}
        fill={COLORS.cyanDim}
        stroke={COLORS.cyan}
        strokeWidth="1.5"
        rx={1}
      />
      {/* Wrap */}
      {state.wrap && (
        <rect
          x={x0 + len * nockEnd}
          y={y - shaftH / 2 - 1}
          width={len * (wrapEnd - nockEnd)}
          height={shaftH + 2}
          fill={COLORS.ember}
          opacity="0.6"
        />
      )}
      {/* Vanes — 3 angular wedges */}
      <g>
        {[0, 1, 2].map((i) => (
          <path
            key={i}
            d={`M ${x0 + len * wrapEnd} ${y + (i - 1) * 4}
                L ${x0 + len * vaneEnd} ${y + (i - 1) * 22}
                L ${x0 + len * vaneEnd} ${y + (i - 1) * 14}
                Z`}
            fill={i === 1 ? COLORS.rose : COLORS.cyan}
            opacity="0.85"
          />
        ))}
      </g>
      {/* Insert */}
      <rect
        x={x0 + len * insertStart}
        y={y - shaftH / 2 - 2}
        width={len * (pointStart - insertStart)}
        height={shaftH + 4}
        fill={COLORS.emberDim}
      />
      {/* Point */}
      <path
        d={`M ${x0 + len * pointStart} ${y - shaftH / 2 - 2}
            L ${x0 + len} ${y}
            L ${x0 + len * pointStart} ${y + shaftH / 2 + 2}
            Z`}
        fill={COLORS.ember}
      />
      {/* Center line */}
      <line
        x1={x0}
        x2={x1}
        y1={y + 50}
        y2={y + 50}
        stroke={COLORS.bgGrid}
        strokeWidth="1"
        strokeDasharray="4 6"
      />
      {/* Length label */}
      <text
        x={(x0 + x1) / 2}
        y={y + 75}
        textAnchor="middle"
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="13"
        fill={COLORS.fgMuted}
        letterSpacing="2"
      >
        {state.length.toFixed(1)}" · {String(state.vaneCount)} vane ·{' '}
        {state.helicalDeg !== undefined ? `${state.helicalDeg}° helical` : '2° helical'}
      </text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Session variant
// ─────────────────────────────────────────────────────────────────────────────

function SessionBody({
  snapshot,
  showLabel,
  showPhoto,
}: {
  snapshot: Extract<ShareSnapshot, { variant: 'session' }>;
  showLabel: boolean;
  showPhoto: boolean;
}) {
  // Target overlay on the left, aggregate stats on the right.
  const targetCx = 280;
  const targetCy = 360;
  const targetR = 220;
  // Inch range to plot — auto-scale to fit the worst shot inside 90% of R.
  const maxInches = Math.max(
    8,
    ...snapshot.shots.map((s) => Math.hypot(s.xIn, s.yIn)),
  );
  const inchScale = (targetR * 0.92) / maxInches;
  return (
    <>
      {showLabel && snapshot.customLabel && (
        <CustomLabel text={snapshot.customLabel} />
      )}
      {/* Target face */}
      <TargetFace
        cx={targetCx}
        cy={targetCy}
        r={targetR}
        photoDataUri={showPhoto ? snapshot.photoDataUri : undefined}
      />
      {/* Shot dots */}
      {snapshot.shots.map((s, i) => (
        <ShotDot
          key={i}
          x={targetCx + s.xIn * inchScale}
          y={targetCy + s.yIn * inchScale}
          color={COLORS.ember}
          ring={i === 0}
        />
      ))}
      {/* Aggregate stats */}
      <g transform={`translate(620, 160)`}>
        <text
          fontFamily="ui-monospace, Menlo, Consolas, monospace"
          fontSize="13"
          letterSpacing="4"
          fill={COLORS.fgDim}
        >
          SESSION SUMMARY
        </text>
        <StatRow y={50} label="Distance" value={`${snapshot.aggregates.distanceYd ?? '—'} yd`} />
        <StatRow y={110} label="Shots" value={String(snapshot.aggregates.count)} />
        <StatRow y={170} label="Mean radius" value={`${format(snapshot.aggregates.meanRadiusIn, 2)}"`} />
        <StatRow y={230} label="95% group" value={`${format(snapshot.aggregates.group95RadiusIn, 2)}"`} />
        {snapshot.session.conditions?.temperatureF !== undefined && (
          <StatRow
            y={290}
            label="Conditions"
            value={`${snapshot.session.conditions.temperatureF}°F · ${snapshot.session.conditions.windMph ?? 0} mph`}
            small
          />
        )}
      </g>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Compare variant
// ─────────────────────────────────────────────────────────────────────────────

function CompareBody({
  snapshot,
  showLabel,
}: {
  snapshot: Extract<ShareSnapshot, { variant: 'compare' }>;
  showLabel: boolean;
}) {
  const targetCx = 280;
  const targetCy = 360;
  const targetR = 220;
  const maxInches = Math.max(
    8,
    ...snapshot.shots.map((s) => Math.hypot(s.xIn, s.yIn)),
  );
  const inchScale = (targetR * 0.92) / maxInches;
  const colorByLabel = new Map(snapshot.sources.map((s) => [s.label, s.color]));
  return (
    <>
      {showLabel && snapshot.customLabel && (
        <CustomLabel text={snapshot.customLabel} />
      )}
      <TargetFace cx={targetCx} cy={targetCy} r={targetR} />
      {snapshot.shots.map((s, i) => (
        <ShotDot
          key={i}
          x={targetCx + s.xIn * inchScale}
          y={targetCy + s.yIn * inchScale}
          color={colorByLabel.get(s.sourceKey ?? '') ?? COLORS.ember}
        />
      ))}
      <g transform={`translate(620, 140)`}>
        <text
          fontFamily="ui-monospace, Menlo, Consolas, monospace"
          fontSize="13"
          letterSpacing="4"
          fill={COLORS.fgDim}
        >
          COMPARISON · {snapshot.aggregates.distanceYd ?? '—'} YD
        </text>
        {snapshot.sources.slice(0, 4).map((src, i) => (
          <CompareLegendRow key={src.label} source={src} y={50 + i * 75} />
        ))}
      </g>
    </>
  );
}

function CompareLegendRow({ source, y }: { source: ShareCompareSource; y: number }) {
  return (
    <g transform={`translate(0, ${y})`}>
      <circle cx={10} cy={10} r={9} fill={source.color} stroke={COLORS.fg} strokeWidth="2" />
      <text
        x={32}
        y={16}
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize="22"
        fill={COLORS.fg}
        fontWeight="600"
      >
        {truncate(source.label, 24)}
      </text>
      <text
        x={32}
        y={46}
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="14"
        fill={COLORS.fgMuted}
        letterSpacing="1"
      >
        {source.count} shots · {format(source.meanRadiusIn, 2)}" mean
      </text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable primitives
// ─────────────────────────────────────────────────────────────────────────────

function TargetFace({
  cx,
  cy,
  r,
  photoDataUri,
}: {
  cx: number;
  cy: number;
  r: number;
  photoDataUri?: string;
}) {
  return (
    <g>
      {/* Optional photo background, clipped to the target disc */}
      {photoDataUri && (
        <>
          <defs>
            <clipPath id="spx-photo-clip">
              <circle cx={cx} cy={cy} r={r} />
            </clipPath>
          </defs>
          <image
            x={cx - r}
            y={cy - r}
            width={r * 2}
            height={r * 2}
            href={photoDataUri}
            clipPath="url(#spx-photo-clip)"
            preserveAspectRatio="xMidYMid slice"
            opacity="0.55"
          />
        </>
      )}
      {/* Concentric scoring rings */}
      {[1, 0.78, 0.55, 0.32, 0.16].map((f, i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r * f}
          fill="none"
          stroke={i === 4 ? COLORS.ember : COLORS.panelStroke}
          strokeWidth={i === 4 ? 2 : 1}
          opacity={photoDataUri ? 0.65 : 1}
        />
      ))}
      <circle cx={cx} cy={cy} r={4} fill={COLORS.ember} />
      {/* Crosshair */}
      <line x1={cx - r} x2={cx + r} y1={cy} y2={cy} stroke={COLORS.bgGrid} strokeWidth="1" />
      <line x1={cx} x2={cx} y1={cy - r} y2={cy + r} stroke={COLORS.bgGrid} strokeWidth="1" />
    </g>
  );
}

function ShotDot({ x, y, color, ring }: { x: number; y: number; color: string; ring?: boolean }) {
  return (
    <g>
      {ring && <circle cx={x} cy={y} r={10} fill="none" stroke={color} strokeWidth="2" opacity="0.4" />}
      <circle cx={x} cy={y} r={6} fill={color} stroke="#fff" strokeWidth="1.5" />
    </g>
  );
}

function StatRow({
  y,
  label,
  value,
  small,
}: {
  y: number;
  label: string;
  value: string;
  small?: boolean;
}) {
  return (
    <g transform={`translate(0, ${y})`}>
      <text
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="13"
        letterSpacing="3"
        fill={COLORS.fgDim}
      >
        {label.toUpperCase()}
      </text>
      <text
        y={32}
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontSize={small ? '24' : '36'}
        fontWeight="700"
        fill={COLORS.fg}
      >
        {value}
      </text>
    </g>
  );
}

function CustomLabel({ text }: { text: string }) {
  return (
    <g transform={`translate(${W - 40}, 50)`}>
      <text
        textAnchor="end"
        fontFamily="ui-monospace, Menlo, Consolas, monospace"
        fontSize="16"
        letterSpacing="2"
        fill={COLORS.fgMuted}
      >
        {truncate(text, 32)}
      </text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// utils
// ─────────────────────────────────────────────────────────────────────────────

function format(n: number, places: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(places);
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}
